/**
 * Resolve a recipient's display emoji using the same fallback chain
 * the board page uses — so the My People tiles + homepage tiles +
 * gift recs board header all surface the same glyph for any given
 * recipient. Kate feedback 2026-05-12: "the emojis on 'my people'
 * and on the homepage should match the emojis on the gift recs pages."
 *
 * Resolution chain:
 *   1. Explicit `emoji` on the recipient (user set / chose).
 *   2. Default for `relationship` per RELATIONSHIPS table (Mom → 🌷, etc.).
 *   3. '✨' sentinel — matches BoardHeader's last-resort fallback.
 *
 * `relationship` may arrive as EITHER the wire enum ('DAD', 'GRANDMA',
 * etc., from a `Recipient` Firestore doc) OR the display string ('Dad',
 * 'Grandma', from in-FE state). RELATIONSHIPS keys by display string —
 * so a raw wire-enum lookup silently misses and the chain falls through
 * to ✨. Translate via DISPLAY_BY_RELATIONSHIP first; for inputs that
 * are already display strings the translation is a no-op (the original
 * relationship is tried as a fallback).
 *
 * The BE materialization (theaWebCommon/materialized_people.py) does
 * the same chain at write time so `materializedPeople.people[i].emoji`
 * is already resolved. This FE helper exists for surfaces that don't
 * read the materialized doc (homepage tiles via useFriendsList).
 */

import { RELATIONSHIPS } from '../../components/landing/quiz/constants';
import { DISPLAY_BY_RELATIONSHIP } from './resultsAdapters';
import type { TheaWebRelationshipEnum } from '../schemas';

export function resolveRecipientEmoji(
  explicit: string | null | undefined,
  relationship: string | null | undefined,
  isMe?: boolean,
): string {
  if (explicit && explicit.trim()) return explicit;
  // Self boards serialize as relationship OTHER + isMe (no ME wire enum),
  // so without this branch a self recipient with no stored emoji falls
  // through to ✨ while the board header shows 🙋 — resolve the same
  // raising hand here. Callers with a recipient doc/snapshot in hand
  // should always pass isMe.
  if (isMe) {
    return RELATIONSHIPS.find((r) => r.value === 'Me!')?.emoji ?? '✨';
  }
  if (relationship) {
    // Try the wire-enum → display translation first (covers raw
    // Firestore docs); fall through to a direct match (covers callers
    // that already pass a display string).
    const display =
      DISPLAY_BY_RELATIONSHIP[relationship as TheaWebRelationshipEnum] ??
      relationship;
    const hit = RELATIONSHIPS.find((r) => r.value === display);
    if (hit) return hit.emoji;
  }
  return '✨';
}
